import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { EditorLayout } from './components/EditorLayout';
import { Sidebar } from './components/home/layout/Sidebar';
import { LoginModal } from './components/auth/LoginModal';
import { HomePage } from './components/home/HomePage';
import { HomeTopBar } from './components/home/layout/HomeTopBar';
import { CanvasPage } from './pages/CanvasPage';
import { DetailPage } from './pages/DetailPage';
import { ToolsPage } from './pages/ToolsPage';
import { FeaturedPreviewPage } from './pages/FeaturedPreviewPage';
import { BongWorksPage } from './pages/BongWorksPage';
import { WorkflowPreviewPage } from './pages/WorkflowPreviewPage';
import { ContentPage } from './pages/ContentPage';
import { useNodeStore } from './stores/nodeStore';
import { useCanvasStore } from './stores/canvasStore';
import { useModelStore } from './stores/modelStore';
import { useTaskStore } from './stores/taskStore';
import { useProjectStore } from './stores/projectStore';
import { useAgentStore } from './stores/agentStore';
import type { PendingSkillMeta } from './hooks/home/useAgentInput';
import { useAuthStore } from './stores/authStore';
import { useWorksStore, selectResolvableWorks } from './stores/worksStore';
import { useFeaturedPreviewStore } from './stores/featuredPreviewStore';
import { resolveFeaturedItem, type FeaturedPreviewItem } from './config/featuredTemplates';
import type { WorkCard } from './components/home/works/workLayout';
import { useDataPersistence } from './hooks/useDataPersistence';
import { useWorkflowManager } from './hooks/useWorkflowManager';
import { useViewTransition } from './hooks/useViewTransition';
import { useActiveSection, type NavItem } from './hooks/home/useActiveSection';
import { useUrlRouting, parseInitialRoute, type InitialRoute } from './hooks/useUrlRouting';
import { OPEN_CONTENT_EVENT } from './utils/siteContent';
// P1-6: sendFirstMessage 双轨合并——删除独立首消息路径，改用 pendingFirstMessage 标志
// 让 AssistantPanel 挂载后复用 useStreamHandler.handleSendMessage 统一发送
import promoItemsData from './config/promo.json';
import { Toaster } from 'sonner';

const SPRING = "cubic-bezier(0.32, 0.72, 0, 1)";
type CurrentView = 'home' | 'editor' | 'canvas' | 'detail' | 'tools' | 'featured' | 'bongworks' | 'content';

export const AppNew = () => {
    // URL 路由：惰性解析初始路由，刷新时从 URL 恢复视图
    const initialRouteRef = useRef(parseInitialRoute());

    const [currentView, setCurrentView] = useState<CurrentView>(initialRouteRef.current.view);
    // 侧边栏折叠状态提升至此：HomePage 内容区 margin-left 需跟随（240px <-> 72px）
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const toggleSidebarCollapsed = useCallback(() => setSidebarCollapsed((c) => !c), []);
    const [isChatOpen, setIsChatOpen] = useState(false);
    const [isMultiFrameOpen, setIsMultiFrameOpen] = useState(false);
    const [isSonicStudioOpen, setIsSonicStudioOpen] = useState(false);
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);
    const [sphereFlowing, setSphereFlowing] = useState(true);

    const [isLoaded, setIsLoaded] = useState(false);
    const [dataLoadSucceeded, setDataLoadSucceeded] = useState(false);

    const nodes = useNodeStore((s: any) => s.nodes);
    const connections = useNodeStore((s: any) => s.connections);
    const setNodes = useNodeStore((s: any) => s.setNodes);
    const setConnections = useNodeStore((s: any) => s.setConnections);
    const loadModels = useModelStore((s: any) => s.loadModels);
    const loadFromLocalStorage = useTaskStore((s: any) => s.loadFromLocalStorage);
    const connectWebSocket = useTaskStore((s: any) => s.connectWebSocket);
    const disconnectWebSocket = useTaskStore((s: any) => s.disconnectWebSocket);
    const projects = useProjectStore((s: any) => s.projects);
    const currentProjectId = useProjectStore((s: any) => s.currentProjectId);
    const loadProjects = useProjectStore((s: any) => s.loadProjects);

    const [assetHistory, setAssetHistory] = useState<any>([]);
    const hasRestoredRef = useRef(false);
    // 跨视图导航时记录待滚动目标，等 home 视图渲染后再执行 scrollIntoView
    const pendingScrollRef = useRef<string | null>(null);

    const { isTransitioning, transitionDirection } = useViewTransition();
    // featured 预览页可能由首页作品区或 Bong作品页进入：侧边栏高亮跟随来源页
    const [featuredOrigin, setFeaturedOrigin] = useState<'home' | 'bongworks'>('home');
    // 站点内容页（页脚/协议链接）：slug 由 CustomEvent 携带或刷新时从 URL query 恢复
    const [contentSlug, setContentSlug] = useState<string | null>(
        initialRouteRef.current.view === 'content' ? initialRouteRef.current.slug ?? null : null
    );
    // 查看制作流程：预览页按钮打开对应作品的只读画布项目（非 null 时覆盖渲染）
    const [workflowPreviewItem, setWorkflowPreviewItem] = useState<FeaturedPreviewItem | null>(null);
    const activeSection = useActiveSection(
        currentView === 'featured' ? featuredOrigin : currentView === 'content' ? 'home' : currentView
    );

    const {
        workflows, setWorkflows, selectedWorkflowId,
        saveCurrentAsWorkflow, onLoadWorkflow, onCreateProject, onBackToProjects,
        deleteWorkflow, renameWorkflow,
    } = useWorkflowManager({ saveHistory: () => {} });

    const { isLoggedIn, openLoginModal, fetchUser } = useAuthStore();

    // 浏览器前进/后退时恢复视图
    const handlePopState = useCallback((route: InitialRoute) => {
        setCurrentView(route.view);
        if (route.view === 'content') {
            setContentSlug(route.slug ?? null);
        }
        if (route.view === 'editor' && route.projectId) {
            onLoadWorkflow(route.projectId).catch(() => setCurrentView('home'));
        }
    }, [onLoadWorkflow]);
    const { syncUrl } = useUrlRouting(handlePopState);

    const { loadDataFromStorage } = useDataPersistence({
        isLoaded, dataLoadSucceeded, isInEditor: currentView === 'editor', currentProjectId,
        nodes, connections, assetHistory, workflows,
        setNodes, setConnections, setAssetHistory, setWorkflows,
        setDataLoadSucceeded, setIsLoaded, getThumbnail: () => '',
    });

    useEffect(() => {
        if (window.bongai) void window.bongai.hasSelectedApiKey().then((hasKey: boolean) => { if (!hasKey) window.bongai?.openSelectKey(); });
        loadModels().catch((e: any) => console.error('[Init] loadModels failed:', e));
        loadFromLocalStorage();
        // 启动时验证 token 有效性：无效则清除登录状态，避免用过期/mock token 请求返回空列表
        fetchUser().catch((e: any) => console.error('[Init] fetchUser failed:', e));
        loadDataFromStorage();
        connectWebSocket();
        // 拉取服务端作品列表 + 存量 localStorage 作品一次性迁移（未登录时内部自动跳过）
        void useWorksStore.getState().hydrateFromServer();
        return () => { disconnectWebSocket(); };
    }, []);

    // 刷新恢复：URL 携带 project/:id 时，等待登录就绪后自动加载该项目
    // 用 hasRestoredRef 守卫，确保只恢复一次，避免 onLoadWorkflow 引用变化导致重复加载
    const restoreRoute = initialRouteRef.current;
    useEffect(() => {
        if (hasRestoredRef.current) return;
        if (restoreRoute.view !== 'editor' || !restoreRoute.projectId) {
            hasRestoredRef.current = true;
            return;
        }
        if (!isLoggedIn) return; // 等待登录就绪，不标记为已恢复
        hasRestoredRef.current = true;
        onLoadWorkflow(restoreRoute.projectId)
            .then(() => {
                setDataLoadSucceeded(true);
                setIsChatOpen(false);
            })
            .catch((e: any) => {
                console.error('[UrlRouting] restore project failed:', e);
                // 恢复失败时回到首页，避免卡在空编辑器
                setCurrentView('home');
                syncUrl('home', null);
            });
    }, [restoreRoute.view, restoreRoute.projectId, isLoggedIn, onLoadWorkflow, syncUrl]);

    // 视图/项目变化时同步 URL（featured 视图携带 type/id query，刷新可恢复预览作品；
    // content 视图携带 slug query，刷新可恢复内容页）
    useEffect(() => {
        const featured = currentView === 'featured' ? useFeaturedPreviewStore.getState().current : null;
        const query = currentView === 'content'
            ? (contentSlug ? `slug=${encodeURIComponent(contentSlug)}` : undefined)
            : featured ? `type=${featured.type}&id=${encodeURIComponent(featured.id)}` : undefined;
        syncUrl(currentView, currentProjectId, query);
    }, [currentView, currentProjectId, contentSlug, syncUrl]);

    // 站点内容页入口（页脚/登录弹窗协议链接）：CustomEvent 携带 slug，切 content 视图
    useEffect(() => {
        const openContent = (e: Event) => {
            const slug = (e as CustomEvent<{ slug?: string }>).detail?.slug;
            if (!slug) return;
            setContentSlug(slug);
            setCurrentView('content');
        };
        window.addEventListener(OPEN_CONTENT_EVENT, openContent);
        return () => window.removeEventListener(OPEN_CONTENT_EVENT, openContent);
    }, []);

    // 登录状态变化后重新加载项目列表（登录/登出后刷新可见项目）
    useEffect(() => {
        if (isLoggedIn) {
            loadProjects().catch((e: any) => console.error('[Auth] reload projects failed:', e));
            // 登录就绪后再拉取一次作品（挂载时未登录会跳过，登录后此处补拉 + 迁移）
            void useWorksStore.getState().hydrateFromServer();
        } else {
            // 登出：项目数据已由 authStore.logout 清空，此处退出需要登录的视图并同步 URL；
            // 重置作品拉取标记，下次登录可重新 hydrate + 迁移
            useWorksStore.setState({ hydrated: false });
            setCurrentView((v) => (v === 'home' ? v : 'home'));
        }
    }, [isLoggedIn]);

    useEffect(() => {
        const interval = setInterval(() => {
            setSphereFlowing(prev => !prev);
        }, 4000);
        return () => clearInterval(interval);
    }, []);

    const navigateTo = useCallback((item: NavItem) => {
        if (item === 'home') {
            if (currentView !== 'home') {
                // home 视图尚未渲染，home 锚点元素还不存在
                // 记录待滚动目标，等 currentView 变为 home 后由 effect 执行滚动
                pendingScrollRef.current = item;
                setCurrentView('home');
            } else {
                const el = document.getElementById(item);
                if (el) {
                    el.scrollIntoView({ behavior: 'smooth' });
                }
            }
            return;
        }

        // Bong 作品页为本地展示（作品数据存于浏览器本地），与首页同为免登录浏览态
        if (item === 'bongworks') {
            setCurrentView('bongworks');
            return;
        }

        if (!isLoggedIn) {
            openLoginModal(() => navigateTo(item));
            return;
        }

        setCurrentView(item as CurrentView);
    }, [currentView, isLoggedIn, openLoginModal]);

    const goHome = useCallback(() => {
        setCurrentView('home');
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    const enterEditor = useCallback(async (workflowId?: string) => {
        if (!isLoggedIn) {
            openLoginModal(() => enterEditor(workflowId));
            return;
        }

        if (workflowId) {
            await onLoadWorkflow(workflowId);
        } else {
            // 新画布 = 全新对话：agentStore 为全局单例，上一画布的消息不清除时，
            // 面板打开即显示旧会话（欢迎页被顶掉）。setSessionId(null) 先清 FE-6
            // 最后会话标记（防刷新后误恢复旧画布会话），resetSession 再清内存态；
            // 须先于 onCreateProject，保证面板挂载时即见欢迎页。
            const agentStore = useAgentStore.getState();
            agentStore.setSessionId(null);
            agentStore.resetSession();
            const title = `画布 ${new Date().toLocaleString('zh-CN', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' })}`;
            await onCreateProject(title);
        }
        setDataLoadSucceeded(true);
        setCurrentView('editor');
        setIsChatOpen(true);
    }, [isLoggedIn, openLoginModal, onLoadWorkflow, onCreateProject]);

    const handleAgentSend = useCallback(async (value: string, skill?: PendingSkillMeta) => {
        if (!isLoggedIn) {
            openLoginModal(() => handleAgentSend(value, skill));
            return;
        }

        // 空文本 + 技能：画布标题用技能名，避免"画布 日期"这类无意义标题
        const title = value.trim().slice(0, 20) || skill?.name || `画布 ${new Date().toLocaleString('zh-CN')}`;
        // P1-6: 不再调用 sendFirstMessage 独立流式路径，改为将 input 存入 store
        // 并设置 pendingFirstMessage 标志，AssistantPanel 挂载后自动调用 handleSendMessage
        // 预设 input 和标志（在进入编辑器之前），确保 AssistantPanel 挂载时即可读取
        const agentStore = useAgentStore.getState();
        // 先重置上一画布残留会话态，再写入本次输入与 pending 标志——
        // 顺序不可颠倒：SESSION_RESET 含 input，后置会清掉刚设置的自动发送内容
        agentStore.setSessionId(null);
        agentStore.resetSession();
        agentStore.setInput(value);
        // 技能显式调用：随流式请求以 skill_id 字段传给后端（短路 LLM 意图解析）；
        // pendingSkillName 供消息渲染 @[skill:id|名称] 标签
        agentStore.setPendingSkillId(skill?.id ?? null);
        agentStore.setPendingSkillName(skill?.name ?? null);
        agentStore.setPendingFirstMessage(true);

        const project = await onCreateProject(title, () => {
            setCurrentView('editor');
            setIsChatOpen(true);
        });

        if (!project) {
            setCurrentView('editor');
            setIsChatOpen(true);
        }
    }, [isLoggedIn, openLoginModal, onCreateProject]);

    const handleCreateProjectFromGallery = useCallback(() => {
        if (!isLoggedIn) {
            openLoginModal(handleCreateProjectFromGallery);
            return;
        }
        enterEditor();
    }, [isLoggedIn, openLoginModal, enterEditor]);

    const handleSelectWorkflow = useCallback(async (id: string) => {
        if (!isLoggedIn) {
            openLoginModal(() => handleSelectWorkflow(id));
            return;
        }
        await onLoadWorkflow(id);
        setDataLoadSucceeded(true);
        setCurrentView('editor');
        setIsChatOpen(false);
    }, [isLoggedIn, openLoginModal, onLoadWorkflow]);

    const handleBackToHome = useCallback(async () => {
        // P1-6: abortControllerRef 已移除——流式 abort 由 AssistantPanel 的 useStreamHandler
        // useEffect cleanup 负责（组件卸载时自动 abort + stopAllPolling）
        await onBackToProjects(() => setCurrentView('home'));
        setIsChatOpen(false);
    }, [onBackToProjects]);

    const handleViewAllCanvases = useCallback(() => {
        if (!isLoggedIn) {
            openLoginModal(handleViewAllCanvases);
            return;
        }
        setCurrentView('canvas');
    }, [isLoggedIn, openLoginModal]);

    // 点击作品卡（首页图像/视频精选区，或 Bong作品页）：解析预览数据（模板卡/用户作品双轨）
    // → 打开预览视图。图像 188:19 / 视频 202:501；预览页不强制登录（与首页浏览态一致）
    const handleOpenWork = useCallback((card: WorkCard, origin: 'home' | 'bongworks' = 'home') => {
        // 合并解析：publishedWorks + 精选会话态（匿名访客精选的服务端作品不在 publishedWorks）
        const item = resolveFeaturedItem(card, selectResolvableWorks(useWorksStore.getState()));
        if (!item) return;
        useFeaturedPreviewStore.getState().open(item);
        setFeaturedOrigin(origin);
        setCurrentView('featured');
    }, []);

    // 预览页左栏作品列表：用户图像作品（最新在前）+ 设计稿模板卡（wf-01..13），
    // 与首页「图像作品」展示栏同源同序——点击左栏缩略图即切换整个作品
    const publishedWorks = useWorksStore((s: any) => s.publishedWorks);
    const featuredImageItems = useMemo<FeaturedPreviewItem[]>(() => {
        const userItems = publishedWorks
            .filter((w: any) => w.type === 'image')
            .map((w: any) => resolveFeaturedItem({ key: `pub-${w.id}`, id: w.id }, publishedWorks))
            .filter(Boolean) as FeaturedPreviewItem[];
        const templateItems = Array.from({ length: 13 }, (_, i) => `wf-${String(i + 1).padStart(2, '0')}`)
            .map((id) => resolveFeaturedItem({ key: id, id }, publishedWorks))
            .filter(Boolean) as FeaturedPreviewItem[];
        return [...userItems, ...templateItems];
    }, [publishedWorks]);

    // 视频作品预览页底部切换条数据源：用户视频作品（最新在前），与 Bong 作品页
    // 视频卡同源同序——点缩略图即切换整个作品（精选模板视频仍走 related 相关视频）
    const featuredVideoItems = useMemo<FeaturedPreviewItem[]>(() => {
        return publishedWorks
            .filter((w: any) => w.type === 'video')
            .map((w: any) => resolveFeaturedItem({ key: `pub-${w.id}`, id: w.id }, publishedWorks))
            .filter(Boolean) as FeaturedPreviewItem[];
    }, [publishedWorks]);

    // 预览页返回：清空预览态并回到来源页（首页或 Bong作品页，两者在预览期间均常挂载
    // 且仅 visibility 隐藏，滚动位原样保留——返回即原地）。
    // 不能只调 close()：FeaturedPreviewPage 的 URL 恢复 effect 发现 query 仍在，
    // 会把刚关闭的作品重新打开（表现为返回按钮「没响应」），必须切换视图让预览组件卸载
    const handleBackFromFeatured = useCallback(() => {
        useFeaturedPreviewStore.getState().close();
        setCurrentView(featuredOrigin);
    }, [featuredOrigin]);

    // 稳定化删除/重命名回调：避免每次 AppNew 渲染生成新引用击穿下游 memo 组件
    const handleDeleteWorkflow = useCallback((id: string) => {
        if (id) deleteWorkflow(id);
    }, [deleteWorkflow]);

    const handleRenameWorkflow = useCallback((id: string, title: string) => {
        renameWorkflow(id, title, workflows, setWorkflows);
    }, [renameWorkflow, workflows, setWorkflows]);

    // 运维平台「运营管理-轮播图」为单一数据源（GET /api/banners），静态 promo.json 仅作
    // 接口失败/空数据时的兜底，保证首页轮播永远有内容可渲染
    const [promoItems, setPromoItems] = useState<any[]>(promoItemsData as any[]);
    useEffect(() => {
        let cancelled = false;
        fetch('/api/banners?position=home_top')
            .then((res) => (res.ok ? res.json() : null))
            .then((data) => {
                if (!cancelled && Array.isArray(data) && data.length > 0) setPromoItems(data);
            })
            .catch(() => {});
        return () => { cancelled = true; };
    }, []);

    const isEditor = currentView === 'editor';

    // 即梦式响应式布局（首页/内页/编辑器全站统一）：页面内容为常规流式布局——
    // 内容列 100% 流式、字号恒定、断点适配，无任何 transform: scale 整页/局部缩放。
    // 编辑器视图（EditorLayout）此前沿用的 1920 基准浮动 UI 反缩放体系已退役：
    // 画布的节点/面板/操作栏/侧边栏等全部 UI 回归原生尺寸流式显示

    // home 视图渲染后，执行跨视图导航遗留的滚动（如预览页返回 / 项目页、工具集页
    // 点「Bong精选」→ 定位 Bong 精选区）。等一帧让 DOM 挂载完成再滚，
    // 避免文档 scrollHeight 尚未更新时 scrollIntoView 停在首页顶部
    useEffect(() => {
        if (currentView !== 'home' || !pendingScrollRef.current) return;
        const target = pendingScrollRef.current;
        pendingScrollRef.current = null;
        const raf = requestAnimationFrame(() => {
            const el = document.getElementById(target);
            if (el) el.scrollIntoView({ behavior: 'smooth' });
        });
        return () => cancelAnimationFrame(raf);
    }, [currentView]);

    return (
        /* w-full 而非 w-screen：100vw 含滚动条宽度，页面垂直滚动时会产生 17px 横向溢出。
           非编辑器分支用 clip 而非 auto：根容器被内容撑高后自身从不滚动（滚动发生在
           body），但 overflow-auto 会使其成为内部 sticky 元素的「最近滚动祖先」，吸附
           参照被截断在这个从不滚动的容器上 → Bong 精选 TAB 行+标题栏吸顶失效、随页面
           滚走；clip 不创建滚动容器，sticky 穿透它参照 body 视口正常吸附（custom-scrollbar
           随之移除——容器已无滚动条，body 滚动条被全局样式置 0） */
        <div className={`w-full bg-[#0a0a0c] ${isEditor ? 'h-screen overflow-hidden' : 'min-h-screen overflow-clip'}`}>
            <Toaster position="top-center" richColors theme="dark" closeButton />

            {/* 侧边栏：原生 fixed，宽 72/240 随折叠切换、高度恒满屏（即梦式流式布局，无整页缩放）。
                「新建画布」与项目区按钮（CanvasGallery → handleCreateProjectFromGallery）完全同源：
                登录门禁 + enterEditor() 新建项目后再进编辑器——此前直绑 enterEditor 会被 onClick
                注入 MouseEvent 当作 workflowId，误走 onLoadWorkflow 分支而非新建画布 */}
            {/* 查看制作流程覆盖层为全屏画布（图二无边栏）：打开时隐藏首页左边栏
                （Sidebar z-200 高于覆盖层 z-170，仅靠层级压不住，须条件卸载） */}
            {!isEditor && !workflowPreviewItem && (
                <Sidebar
                    activeItem={activeSection}
                    onNavigate={navigateTo}
                    onGoHome={goHome}
                    onCreateCanvas={handleCreateProjectFromGallery}
                    collapsed={sidebarCollapsed}
                    onToggleCollapsed={toggleSidebarCollapsed}
                />
            )}

            {/* 顶部用户栏（217:1121）：原生 fixed，左缘贴侧边栏右缘、右缘贴视口右缘。
                home 与内页（canvas/detail/tools）统一渲染同款顶栏——内页此前无顶栏，
                与首页/预览页视觉断裂（「内页未同步」）；featured 预览页由
                FeaturedPreviewPage 自带同款顶栏（左端 leading 放返回按钮），不在此重复挂载 */}
            {!isEditor && currentView !== 'featured' && (
                <HomeTopBar collapsed={sidebarCollapsed} />
            )}

            {/* 登录弹窗：常规响应式布局（≥768 双栏固定卡 / 小屏单列流式），无需整页缩放系数 */}
            <LoginModal />

            {/* home + bongworks + featured 常挂载：预览页打开时来源页仅 visibility 隐藏、
                布局与文档滚动位原样保留（文档高度不塌、scrollY 不归零）——返回时预览层一撤，
                页面原地就是离开时的位置，不再出现「先见页面顶部、再平滑滚动定位」的切换过程；
                canvas/detail/tools 等视图仍照旧卸载首页。首页精选区与 Bong作品页按预览来源
                二选一挂载，避免两个流式页面在文档流中纵向堆叠 */}
            {(currentView === 'home' || (currentView === 'featured' && featuredOrigin === 'home')) && (
                <div
                    className={`w-full ${isTransitioning && transitionDirection === 'toEditor' ? 'opacity-0' : 'opacity-100'}`}
                    style={{
                        marginLeft: sidebarCollapsed ? 72 : 240,
                        // 设计稿 1-9：内容列 1400 居中于「侧边栏右侧剩余空间」而非整个视口，
                        // 故宽度须扣除侧边栏（240/72），否则 1400 列整体右移 ~120px、右缘贴边
                        width: `calc(100% - ${sidebarCollapsed ? 72 : 240}px)`,
                        transition: `margin-left 0.25s ${SPRING}, width 0.25s ${SPRING}, opacity 0.5s ${SPRING}`,
                        visibility: currentView === 'featured' ? 'hidden' : 'visible',
                    }}
                >
                    <HomePage
                        promoItems={promoItems}
                        workflows={workflows}
                        projects={projects}
                        onAgentSend={handleAgentSend}
                        onSelectWorkflow={handleSelectWorkflow}
                        onCreateProject={handleCreateProjectFromGallery}
                        onDeleteWorkflow={handleDeleteWorkflow}
                        onRenameWorkflow={handleRenameWorkflow}
                        onViewAllCanvases={handleViewAllCanvases}
                        onOpenWork={(card) => handleOpenWork(card, 'home')}
                        sidebarCollapsed={sidebarCollapsed}
                    />
                </div>
            )}

            {(currentView === 'bongworks' || (currentView === 'featured' && featuredOrigin === 'bongworks')) && (
                /* 与首页同规则：宽度扣除侧边栏（240/72），内容在剩余空间内流式居中 */
                <div
                    className={`w-full ${isTransitioning && transitionDirection === 'toEditor' ? 'opacity-0' : 'opacity-100'}`}
                    style={{
                        marginLeft: sidebarCollapsed ? 72 : 240,
                        width: `calc(100% - ${sidebarCollapsed ? 72 : 240}px)`,
                        transition: `margin-left 0.25s ${SPRING}, width 0.25s ${SPRING}, opacity 0.5s ${SPRING}`,
                        visibility: currentView === 'featured' ? 'hidden' : 'visible',
                    }}
                >
                    <BongWorksPage onOpenWork={(card) => handleOpenWork(card, 'bongworks')} />
                </div>
            )}

            {(currentView === 'canvas' || currentView === 'detail' || currentView === 'tools' || currentView === 'content') && (
                /* 与 home 内容列同规则：宽度扣除侧边栏（240/72），内容在剩余空间内流式居中 */
                <div
                    style={{
                        marginLeft: sidebarCollapsed ? 72 : 240,
                        width: `calc(100% - ${sidebarCollapsed ? 72 : 240}px)`,
                        transition: 'margin-left 0.25s cubic-bezier(0.32, 0.72, 0, 1), width 0.25s cubic-bezier(0.32, 0.72, 0, 1)',
                    }}
                >
                    {currentView === 'canvas' && (
                        <CanvasPage
                            workflows={workflows}
                            projects={projects}
                            onSelectWorkflow={handleSelectWorkflow}
                            onCreateProject={handleCreateProjectFromGallery}
                            onDeleteWorkflow={handleDeleteWorkflow}
                            onRenameWorkflow={handleRenameWorkflow}
                            onBack={goHome}
                        />
                    )}
                    {currentView === 'detail' && <DetailPage />}
                    {currentView === 'tools' && <ToolsPage />}
                    {currentView === 'content' && <ContentPage slug={contentSlug} />}
                </div>
            )}

            {/* Bong 精选作品预览（图像 188:19 / 视频 202:501）：设计稿等比 +
                三段贴边弹性布局——画布按 min(可视宽/1920, 可视高/1080) fit 缩放
                （完整显示无需滚动），左侧锚定侧边栏右缘、宽度拉伸填满剩余区域：
                左栏贴左、右栏贴右、中段在剩余空间居中；侧边栏折叠时同步收窄。
                层级：首页吸顶条 portal z-150 < 预览页 z-[160] < 侧边栏 z-[200] < LoginModal z-[300]
                （首页常挂载后，其作品区吸顶条 portal 挂在 body 上、不受 visibility:hidden 影响，
                预览页层级必须压过它，否则吸顶条会浮在预览页上方）*/}
            {currentView === 'featured' && (
                <FeaturedPreviewPage
                    sidebarCollapsed={sidebarCollapsed}
                    items={featuredImageItems}
                    videoItems={featuredVideoItems}
                    onBack={handleBackFromFeatured}
                    onOpenWorkflow={setWorkflowPreviewItem}
                />
            )}

            {/* 查看制作流程：只读画布项目覆盖层（z-170 > 预览页 z-160）；复制项目后
                经 handleSelectWorkflow 进入编辑器（登录门禁内聚在该回调） */}
            {workflowPreviewItem && (
                <WorkflowPreviewPage
                    item={workflowPreviewItem}
                    onClose={() => setWorkflowPreviewItem(null)}
                    onCopyProject={handleSelectWorkflow}
                />
            )}

            {isEditor && (
                <EditorLayout
                    isTransitioning={isTransitioning}
                    transitionDirection={transitionDirection}
                    onBackToProjects={handleBackToHome}
                    workflows={workflows}
                    selectedWorkflowId={selectedWorkflowId}
                    onSelectWorkflow={(id) => { onLoadWorkflow(id); }}
                    onSaveWorkflow={saveCurrentAsWorkflow}
                    onDeleteWorkflow={handleDeleteWorkflow}
                    onRenameWorkflow={handleRenameWorkflow}
                    onOpenSettings={() => setIsSettingsOpen(true)}
                    isSettingsOpen={isSettingsOpen}
                    onCloseSettings={() => setIsSettingsOpen(false)}
                    isChatOpen={isChatOpen}
                    onToggleChat={() => setIsChatOpen(!isChatOpen)}
                    isMultiFrameOpen={isMultiFrameOpen}
                    onToggleMultiFrame={() => setIsMultiFrameOpen(!isMultiFrameOpen)}
                    isSonicStudioOpen={isSonicStudioOpen}
                    onToggleSonicStudio={() => setIsSonicStudioOpen(!isSonicStudioOpen)}
                    assetHistory={assetHistory}
                    setAssetHistory={setAssetHistory}
                    sphereFlowing={sphereFlowing}
                />
            )}
        </div>
    );
};
